import { useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import Navbar from "./components/Navbar";
import FentaSafeDashboard from "./FentaSafeDashboard";
import LiveAccess from "./pages/LiveAccess";
import OtpLogin from "./OtpLogin";
import UseCases from "./pages/UseCases";

import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import PublicSafety from "./pages/PublicSafety";
import LiveOverview from "./pages/LiveOverview";
import Technology from "./pages/Technology";
import AICommandCenter from "./pages/AICommandCenter";
import Dashboard from "./pages/commandcenter/Dashboard";
import AIOperations from "./pages/commandcenter/AIOperations";
import ThreatZones from "./pages/commandcenter/ThreatZones";
import Governance from "./pages/commandcenter/Governance";
import Forecasting from "./pages/commandcenter/Forecasting";
import AICommandCenterPreview from "./pages/AICommandCenterPreview";
import MissionControl from "./pages/commandcenter/MissionControl";
import Telemetry from "./pages/commandcenter/Telemetry";
import IncidentArchive from "./pages/commandcenter/IncidentArchive";
import { TelemetryProvider } from "./context/TelemetryContext";
import { IndustryProvider } from "./context/IndustryContext";
import ResearchValidation from "./pages/commandcenter/ResearchValidation";
import ProtectedRoute from "./security/ProtectedRoute";
import { ROLES } from "./security/roles";
import LoginPage from "./LoginPage";
import LiveProtectedRoute from "./components/LiveProtectedRoute";
import { getUserRole } from "./security/roleManager";

// ─── Role helpers ──────────────────────────────────────────────────
const PAID_ROLES = [
  ROLES.CUSTOMER_USER,
  ROLES.CUSTOMER_ADMIN,
  ROLES.FENTASAFE_ADMIN,
];

const COMMAND_CENTER_ROLES = [
  ROLES.DEMO_USER,
  ROLES.CUSTOMER_USER,
  ROLES.CUSTOMER_ADMIN,
  ROLES.FENTASAFE_ADMIN,
];

export default function App() {
  const [authenticated, setAuthenticated] = useState(
    !!localStorage.getItem("FENTASAFE_DEMO_TOKEN")
  );

  const role = getUserRole();
  const isPaidUser = PAID_ROLES.includes(role);
  const isDemoUser = role === ROLES.DEMO_USER;

  // ─── Banner logic ──────────────────────────────────────────────
  // Public visitors: no banner
  // Demo users: "simulated" banner
  // Paid users: "live data" banner
  const renderBanner = () => {
    if (isPaidUser) {
      return (
        <div style={{ ...styles.banner, ...styles.liveBanner }}>
          <strong>
            🟢 Live Data — Real-time sensor intelligence from your deployed environment.
          </strong>
        </div>
      );
    }
    if (isDemoUser) {
      return (
        <div style={styles.banner}>
          <strong>
            Interactive Demonstration — Data streams are simulated to illustrate the FentaSafe safety intelligence platform.
          </strong>
        </div>
      );
    }
    return null; // Public visitors see no banner
  };

  return (
    <IndustryProvider>
      <TelemetryProvider>
        <BrowserRouter>
          <div style={styles.appContainer}>

            <Navbar />

            {renderBanner()}

            <main style={styles.main}>
              <Routes>
                {/* ── Public routes ── */}
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/technology" element={<Technology />} />
                <Route path="/use-cases" element={<UseCases />} />
                <Route path="/public-safety" element={<PublicSafety />} />
                <Route path="/login" element={<LoginPage />} />

                {/* ── Demo route (OTP login → DEMO_USER only) ── */}
                <Route
                  path="/demo"
                  element={
                    authenticated ? (
                      <FentaSafeDashboard mode="demo" />
                    ) : (
                      <OtpLogin
                        onSuccess={() => {
                          localStorage.setItem("FENTASAFE_DEMO_TOKEN", "true");
                          localStorage.setItem("FENTASAFE_ROLE", "DEMO_USER");
                          localStorage.setItem(
                            "FENTASAFE_USER",
                            JSON.stringify({
                              role: "DEMO_USER",
                              access_level: "DEMO",
                              data_source: "SIMULATED",
                            })
                          );
                          setAuthenticated(true);
                        }}
                      />
                    )
                  }
                />

                {/* ── Live routes (paid users only) ── */}
                <Route path="/live" element={<LiveAccess />} />
                <Route
                  path="/live/overview"
                  element={
                    <LiveProtectedRoute>
                      <LiveOverview />
                    </LiveProtectedRoute>
                  }
                />
                <Route
                  path="/live/dashboard"
                  element={
                    <LiveProtectedRoute>
                      <FentaSafeDashboard mode="live" />
                    </LiveProtectedRoute>
                  }
                />

                {/* ── AI Command Center (gate entry point) ── */}
                <Route
                  path="/ai-command-center"
                  element={
                    COMMAND_CENTER_ROLES.includes(role)
                      ? <AICommandCenter />
                      : <AICommandCenterPreview />
                  }
                />

                {/* ── AI Command Center sub-routes ──
                    ALL protected: only COMMAND_CENTER_ROLES can access.
                    Previously these were completely open — anyone who
                    knew the URL could access them directly.
                    FIX: wrap every sub-route with ProtectedRoute.        */}
                <Route
                  path="/ai-command-center/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={COMMAND_CENTER_ROLES}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-command-center/ai-operations"
                  element={
                    <ProtectedRoute allowedRoles={COMMAND_CENTER_ROLES}>
                      <AIOperations />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-command-center/threat-zones"
                  element={
                    <ProtectedRoute allowedRoles={COMMAND_CENTER_ROLES}>
                      <ThreatZones />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-command-center/governance"
                  element={
                    <ProtectedRoute allowedRoles={COMMAND_CENTER_ROLES}>
                      <Governance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-command-center/forecasting"
                  element={
                    <ProtectedRoute allowedRoles={COMMAND_CENTER_ROLES}>
                      <Forecasting />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-command-center/mission-control"
                  element={
                    <ProtectedRoute allowedRoles={COMMAND_CENTER_ROLES}>
                      <MissionControl />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-command-center/telemetry"
                  element={
                    <ProtectedRoute allowedRoles={COMMAND_CENTER_ROLES}>
                      <Telemetry />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-command-center/incident-archive"
                  element={
                    <ProtectedRoute allowedRoles={COMMAND_CENTER_ROLES}>
                      <IncidentArchive />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-command-center/research-validation"
                  element={
                    <ProtectedRoute allowedRoles={COMMAND_CENTER_ROLES}>
                      <ResearchValidation />
                    </ProtectedRoute>
                  }
                />

              </Routes>
            </main>

          </div>
        </BrowserRouter>
      </TelemetryProvider>
    </IndustryProvider>
  );
}

const styles = {
  appContainer: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    background: "#0f172a",
  },

  banner: {
    background: "rgba(37, 99, 235, 0.12)",
    color: "#93c5fd",
    textAlign: "center",
    padding: "10px 12px",
    fontSize: "14px",
    borderBottom: "1px solid rgba(37, 99, 235, 0.3)",
  },

  // Override for paid/live users — green instead of blue
  liveBanner: {
    background: "rgba(16, 185, 129, 0.12)",
    color: "#6ee7b7",
    borderBottom: "1px solid rgba(16, 185, 129, 0.3)",
  },

  main: {
    flex: 1,
    padding: "20px",
    overflow: "auto",
  },
};
